import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  Plus,
  Search,
  Edit,
  Trash2,
  Shield,
  UserCheck,
  UserX,
  Crown,
  User,
  Mail,
  Calendar,
  Eye,
  Save,
  X,
  AlertTriangle,
  Key,
  LogIn,
  FileText,
  Building2
} from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gql } from '@/apollo';
import { apolloClient } from '../../services/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { useApplicationSettings } from '../../contexts/ApplicationSettingsContext';
import { GET_SYSTEM_INFO } from '../../graphql/system-info';
import { Tooltip } from '../ui/Tooltip';
import { Modal } from '../ui/Modal';
import { UserCustomerAccessModal } from './UserCustomerAccessModal';

// GraphQL Queries and Mutations
const GET_USERS = gql`
  query GetUsers {
    users {
      id
      username
      email
      firstName
      lastName
      role
      isActive
      canAccessDemo
      canAccessPreProd
      lastLoginAt
      createdAt
      customerAccessCount
    }
  }
`;

const CREATE_USER = gql`
  mutation CreateUser($input: CreateUserInput!) {
    createUser(input: $input) {
      id
      username
      email
      firstName
      lastName
      role
      isActive
      createdAt
    }
  }
`;

const CHECK_USER_AVAILABILITY = gql`
  query CheckUserAvailability($email: String!, $username: String!) {
    checkUserAvailability(email: $email, username: $username) {
      emailTaken
      usernameTaken
    }
  }
`;

const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UpdateUserInput!) {
    updateUser(id: $id, input: $input) {
      id
      username
      email
      firstName
      lastName
      role
      isActive
      canAccessDemo
      canAccessPreProd
      createdAt
    }
  }
`;

const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

const RESET_USER_PASSWORD = gql`
  mutation ResetUserPassword($id: ID!) {
    resetUserPassword(id: $id) {
      success
      message
      emailSent
    }
  }
`;

const GET_USER_NOTES = gql`
  query GetUserNotes($userId: ID!) {
    userNotes(userId: $userId) {
      id
      title
      content
      isPublic
      tags
      createdAt
      updatedAt
      canEdit
      author {
        id
        username
        firstName
        lastName
      }
      sharedWith {
        id
        canEdit
        user {
          id
          username
          firstName
          lastName
        }
      }
    }
  }
`;

const DELETE_NOTE = gql`
  mutation DeleteNote($id: ID!) {
    deleteNote(id: $id)
  }
`;

const SHARE_NOTE = gql`
  mutation ShareNote($noteId: ID!, $input: ShareNoteInput!) {
    shareNote(noteId: $noteId, input: $input) {
      id
      canEdit
      user {
        id
        username
        firstName
        lastName
      }
    }
  }
`;

interface CreateFieldErrors {
  email?: string;
  username?: string;
}

function parseCreateUserFieldErrors(err: any): {
  fieldErrors: CreateFieldErrors;
  general?: string;
} {
  const gqlError = err?.graphQLErrors?.[0];
  const extensions = gqlError?.extensions ?? {};
  const fieldErrors: CreateFieldErrors = {};

  if (extensions.emailTaken) {
    fieldErrors.email = 'This email is already in use';
  }
  if (extensions.usernameTaken) {
    fieldErrors.username = 'This username is already in use';
  }
  if (extensions.field === 'email') {
    fieldErrors.email = gqlError?.message || fieldErrors.email || 'This email is already in use';
  }
  if (extensions.field === 'username') {
    fieldErrors.username = gqlError?.message || fieldErrors.username || 'This username is already in use';
  }

  const hasFieldErrors = Boolean(fieldErrors.email || fieldErrors.username);
  return {
    fieldErrors,
    general: hasFieldErrors
      ? undefined
      : gqlError?.message || err?.message || 'Failed to create user',
  };
}

interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'MODERATOR' | 'ADMINISTRATOR';
  authProvider?: 'LOCAL' | 'ENTRA_AD';
  isActive: boolean;
  emailVerified?: boolean;
  canAccessDemo?: boolean;
  canAccessPreProd?: boolean;
  lastLoginAt?: string;
  createdAt: string;
  updatedAt?: string;
  customerAccessCount?: number;
}

interface CreateUserInput {
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  role: 'USER' | 'MODERATOR' | 'ADMINISTRATOR';
}

interface UpdateUserInput {
  username?: string;
  email?: string;
  firstName?: string;
  lastName?: string;
  role?: 'USER' | 'MODERATOR' | 'ADMINISTRATOR';
  isActive?: boolean;
  canAccessDemo?: boolean;
  canAccessPreProd?: boolean;
}

interface Note {
  id: string;
  title: string;
  content: string;
  isPublic: boolean;
  tags: string[];
  createdAt: string;
  updatedAt: string;
  canEdit: boolean;
  author: {
    id: string;
    username: string;
    firstName?: string;
    lastName?: string;
  };
  sharedWith: Array<{
    id: string;
    canEdit: boolean;
    user: {
      id: string;
      username: string;
      firstName?: string;
      lastName?: string;
    };
  }>;
}

const ROLES = [
  { value: 'USER', label: 'User', icon: User, color: '#2563eb' },
  { value: 'MODERATOR', label: 'Moderator', icon: Shield, color: '#ea580c' },
  { value: 'ADMINISTRATOR', label: 'Administrator', icon: Crown, color: '#9333ea' }
];

// Auth provider is set automatically by the server (LOCAL by default)

export const UserManagement: React.FC = () => {
  const { user: currentUser } = useAuth();
  const { settings } = useApplicationSettings();
  const queryClient = useQueryClient();
  
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('ALL');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [showCustomerAccessModal, setShowCustomerAccessModal] = useState(false);
  const [showNoteDetailsModal, setShowNoteDetailsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [selectedNote, setSelectedNote] = useState<Note | null>(null);
  const [shareForm, setShareForm] = useState({
    userId: '',
    canEdit: false
  });
  const [passwordResetMessage, setPasswordResetMessage] = useState<string | null>(null);
  const [passwordResetError, setPasswordResetError] = useState<string | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [createFieldErrors, setCreateFieldErrors] = useState<CreateFieldErrors>({});

  const [createForm, setCreateForm] = useState<CreateUserInput>({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    role: 'USER'
  });

  const [editForm, setEditForm] = useState<UpdateUserInput>({});

  // Fetch users
  const { data: usersData, isLoading, error, refetch } = useQuery({
    queryKey: ['users'],
    queryFn: async () => {
      if (!currentUser || currentUser.role !== 'ADMINISTRATOR') {
        throw new Error('Unauthorized: Administrator role required');
      }
      const { data } = await apolloClient.query({
        query: GET_USERS,
        fetchPolicy: 'no-cache',
        errorPolicy: 'all'
      });
      return data.users as User[];
    },
    enabled: !!currentUser && currentUser.role === 'ADMINISTRATOR'
  });

  // Fetch system info to get environment name
  const { data: systemInfoData } = useQuery({
    queryKey: ['systemInfo'],
    queryFn: async () => {
      const { data } = await apolloClient.query({
        query: GET_SYSTEM_INFO,
        fetchPolicy: 'cache-first',
        errorPolicy: 'ignore'
      });
      return data;
    }
  });

  const environmentName = systemInfoData?.systemInfo?.application?.environmentName || '';

  // Fetch user notes when notes modal is open
  const { data: userNotesData, isLoading: notesLoading } = useQuery({
    queryKey: ['userNotes', selectedUser?.id],
    queryFn: async () => {
      if (!selectedUser?.id) return [];
      try {
        const { data } = await apolloClient.query({
          query: GET_USER_NOTES,
          variables: { userId: selectedUser.id },
          fetchPolicy: 'no-cache'
        });
        return data.userNotes || [];
      } catch (error) {
        return [];
      }
    },
    enabled: !!selectedUser?.id && showNotesModal,
    initialData: []
  });

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: async (input: CreateUserInput) => {
      const { data } = await apolloClient.mutate({
        mutation: CREATE_USER,
        variables: { input },
        errorPolicy: 'none',
      });

      if (!data?.createUser) {
        throw new Error('Failed to create user');
      }

      return data.createUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowCreateModal(false);
      resetCreateForm();
      setCreateError(null);
      setCreateFieldErrors({});
    },
    onError: (err: any) => {
      const { fieldErrors, general } = parseCreateUserFieldErrors(err);
      if (fieldErrors.email || fieldErrors.username) {
        setCreateFieldErrors(fieldErrors);
        setCreateError(null);
        return;
      }
      setCreateFieldErrors({});
      setCreateError(general || 'Failed to create user');
    }
  });

  const updateUserMutation = useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateUserInput }) => {
      const { data } = await apolloClient.mutate({ mutation: UPDATE_USER, variables: { id, input } });
      return data.updateUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowEditModal(false);
      setSelectedUser(null);
      setEditForm({});
    }
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apolloClient.mutate({ mutation: DELETE_USER, variables: { id } });
      return data.deleteUser;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] });
      setShowDeleteModal(false);
      setSelectedUser(null);
    }
  });

  const resetUserPasswordMutation = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await apolloClient.mutate({
        mutation: RESET_USER_PASSWORD,
        variables: { id },
      });
      return data.resetUserPassword;
    },
    onSuccess: (result) => {
      setPasswordResetMessage(result.message);
      setPasswordResetError(null);
      setShowPasswordModal(false);
      setSelectedUser(null);
    },
    onError: (error: Error) => {
      setPasswordResetError(error.message || 'Failed to reset password');
    },
  });

  // Note mutations
  const deleteNoteMutation = useMutation({
    mutationFn: async (noteId: string) => {
      const { data } = await apolloClient.mutate({ mutation: DELETE_NOTE, variables: { id: noteId } });
      return data.deleteNote;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotes', selectedUser?.id] });
      setShowDeleteConfirmModal(false);
      setShowNoteDetailsModal(false);
      setSelectedNote(null);
    }
  });

  const shareNoteMutation = useMutation({
    mutationFn: async ({ noteId, userId, canEdit }: { noteId: string; userId: string; canEdit: boolean }) => {
      const { data } = await apolloClient.mutate({
        mutation: SHARE_NOTE,
        variables: { noteId, input: { userId, permission: canEdit ? 'WRITE' : 'READ' } }
      });
      return data.shareNote;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['userNotes', selectedUser?.id] });
      setShowShareModal(false);
      setShareForm({ userId: '', canEdit: false });
    }
  });

  const resetCreateForm = () => {
    setCreateForm({ username: '', email: '', firstName: '', lastName: '', role: 'USER' });
    setCreateError(null);
    setCreateFieldErrors({});
  };

  const applyAvailabilityErrors = (emailTaken: boolean, usernameTaken: boolean) => {
    const fieldErrors: CreateFieldErrors = {};
    if (emailTaken) {
      fieldErrors.email = 'This email is already in use';
    }
    if (usernameTaken) {
      fieldErrors.username = 'This username is already in use';
    }
    setCreateFieldErrors(fieldErrors);
    setCreateError(null);
    return !(emailTaken || usernameTaken);
  };

  const checkLocalUserAvailability = (email: string, username: string) => {
    const normalizedEmail = email.trim().toLowerCase();
    const normalizedUsername = username.trim().toLowerCase();
    let emailTaken = false;
    let usernameTaken = false;

    usersData?.forEach((user) => {
      if (user.email.trim().toLowerCase() === normalizedEmail) {
        emailTaken = true;
      }
      if (user.username.trim().toLowerCase() === normalizedUsername) {
        usernameTaken = true;
      }
    });

    return { emailTaken, usernameTaken };
  };

  const validateCreateUserAvailability = async () => {
    const email = createForm.email.trim();
    const username = createForm.username.trim();
    if (!email || !username) {
      return false;
    }

    const local = checkLocalUserAvailability(email, username);
    if (local.emailTaken || local.usernameTaken) {
      return applyAvailabilityErrors(local.emailTaken, local.usernameTaken);
    }

    try {
      const { data } = await apolloClient.query({
        query: CHECK_USER_AVAILABILITY,
        variables: { email, username },
        fetchPolicy: 'network-only',
      });
      const availability = data?.checkUserAvailability;
      if (!availability) {
        return true;
      }
      return applyAvailabilityErrors(availability.emailTaken, availability.usernameTaken);
    } catch {
      return true;
    }
  };

  const handleCreateFieldBlur = async (field: 'email' | 'username') => {
    const email = createForm.email.trim();
    const username = createForm.username.trim();
    if (!email && !username) {
      return;
    }

    const local = checkLocalUserAvailability(email, username);
    if (local.emailTaken || local.usernameTaken) {
      applyAvailabilityErrors(local.emailTaken, local.usernameTaken);
      return;
    }

    if (field === 'email' && !email) return;
    if (field === 'username' && !username) return;

    try {
      const { data } = await apolloClient.query({
        query: CHECK_USER_AVAILABILITY,
        variables: { email: email || ' ', username: username || ' ' },
        fetchPolicy: 'network-only',
      });
      const availability = data?.checkUserAvailability;
      if (availability) {
        applyAvailabilityErrors(availability.emailTaken, availability.usernameTaken);
      }
    } catch {
      // Ignore availability check errors on blur
    }
  };

  const handleCreateUser = async () => {
    setCreateError(null);
    const isAvailable = await validateCreateUserAvailability();
    if (!isAvailable) {
      return;
    }
    createUserMutation.mutate(createForm);
  };

  const handleUpdateUser = () => {
    if (selectedUser) {
      updateUserMutation.mutate({ id: selectedUser.id, input: editForm });
    }
  };

  const handleDeleteUser = () => {
    if (selectedUser) {
      deleteUserMutation.mutate(selectedUser.id);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditForm({ 
      username: user.username, 
      email: user.email, 
      firstName: user.firstName || '', 
      lastName: user.lastName || '', 
      role: user.role, 
      isActive: user.isActive,
      canAccessDemo: user.canAccessDemo || false,
      canAccessPreProd: user.canAccessPreProd || false
    });
    setShowEditModal(true);
  };

  const openDeleteModal = (user: User) => {
    setSelectedUser(user);
    setShowDeleteModal(true);
  };

  const openPasswordModal = (user: User) => {
    setSelectedUser(user);
    setPasswordResetMessage(null);
    setPasswordResetError(null);
    setShowPasswordModal(true);
  };

  const openNotesModal = (user: User) => {
    setSelectedUser(user);
    setShowNotesModal(true);
  };

  const openCustomerAccessModal = (user: User) => {
    setSelectedUser(user);
    setShowCustomerAccessModal(true);
  };

  const handlePasswordChange = () => {
    if (selectedUser) {
      resetUserPasswordMutation.mutate(selectedUser.id);
    }
  };

  const formatDate = (dateString: string | null | undefined): string => {
    if (!dateString) return 'Never';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'Invalid date';
      return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
    } catch {
      return 'Invalid date';
    }
  };

  const toggleUserStatus = (user: User) => {
    updateUserMutation.mutate({ id: user.id, input: { isActive: !user.isActive } });
  };

  const filteredUsers = usersData?.filter(user => {
    const matchesSearch = 
      user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.firstName && user.firstName.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.lastName && user.lastName.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesRole = roleFilter === 'ALL' || user.role === roleFilter;
    const matchesStatus = statusFilter === 'ALL' || (statusFilter === 'ACTIVE' && user.isActive) || (statusFilter === 'INACTIVE' && !user.isActive);
    return matchesSearch && matchesRole && matchesStatus;
  }) || [];

  const getRoleIcon = (role: string): React.ReactElement => {
    const roleConfig = ROLES.find(r => r.value === role);
    if (!roleConfig) return <User className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />;
    const Icon = roleConfig.icon;
    return <Icon className="w-4 h-4" style={{ color: roleConfig.color }} />;
  };

  const getRoleColor = (role: string) => {
    const roleConfig = ROLES.find(r => r.value === role);
    return roleConfig?.color || 'var(--color-text-muted)';
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
        <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading users...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-error)' }} />
        <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>Error Loading Users</h3>
        <p className="mb-2" style={{ color: 'var(--color-text-secondary)' }}>Failed to load user data. Please try again.</p>
        {error && (
          <div className="mb-4 p-4 rounded-lg text-left max-w-md mx-auto" style={{ backgroundColor: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)' }}>
            <p className="text-sm font-mono" style={{ color: 'var(--color-error-text)' }}>
              {error instanceof Error ? error.message : String(error)}
            </p>
          </div>
        )}
        <button onClick={() => refetch()} className="px-4 py-2 text-white rounded-lg transition-colors" style={{ backgroundColor: 'var(--color-accent)' }}>Retry</button>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-full overflow-x-hidden">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-2xl font-bold flex items-center" style={{ color: 'var(--color-text-primary)' }}>
            <Users className="w-7 h-7 text-indigo-600 mr-3" />
            User Management
          </h2>
          <p className="mt-1" style={{ color: 'var(--color-text-secondary)' }}>
            Manage user accounts, roles, and permissions
          </p>
        </div>
        <Tooltip content="Create a new user account">
          <button
            onClick={() => { setShowCreateModal(true); setCreateError(null); }}
            className="flex items-center px-4 py-2 text-white rounded-lg transition-colors"
            style={{
              backgroundColor: 'var(--color-accent)'
            }}
          >
            <Plus className="w-4 h-4 mr-2" />
            Add User
          </button>
        </Tooltip>
      </div>

      {passwordResetMessage && (
        <div
          className="flex items-start justify-between gap-4 px-4 py-3 rounded-lg text-sm"
          style={{
            backgroundColor: 'rgba(34, 197, 94, 0.1)',
            border: '1px solid rgba(34, 197, 94, 0.3)',
            color: 'var(--color-text-primary)',
          }}
        >
          <p>{passwordResetMessage}</p>
          <button
            type="button"
            onClick={() => setPasswordResetMessage(null)}
            className="shrink-0 hover:opacity-70"
            style={{ color: 'var(--color-text-muted)' }}
            aria-label="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <div 
          className="p-6 rounded-xl shadow-sm border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div className="flex items-center">
            <Users className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Total Users</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>{usersData?.length || 0}</p>
            </div>
          </div>
        </div>
        <div 
          className="p-6 rounded-xl shadow-sm border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div className="flex items-center">
            <UserCheck className="w-8 h-8" style={{ color: 'var(--color-success)' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Active Users</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {usersData?.filter(u => u.isActive).length || 0}
              </p>
            </div>
          </div>
        </div>
        <div 
          className="p-6 rounded-xl shadow-sm border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div className="flex items-center">
            <Crown className="w-8 h-8" style={{ color: 'var(--color-accent)' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Administrators</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {usersData?.filter(u => u.role === 'ADMINISTRATOR').length || 0}
              </p>
            </div>
          </div>
        </div>
        <div 
          className="p-6 rounded-xl shadow-sm border"
          style={{
            backgroundColor: 'var(--color-bg-primary)',
            borderColor: 'var(--color-border)',
            boxShadow: 'var(--shadow-sm)'
          }}
        >
          <div className="flex items-center">
            <Shield className="w-8 h-8" style={{ color: 'var(--color-warning)' }} />
            <div className="ml-4">
              <p className="text-sm font-medium" style={{ color: 'var(--color-text-secondary)' }}>Moderators</p>
              <p className="text-2xl font-bold" style={{ color: 'var(--color-text-primary)' }}>
                {usersData?.filter(u => u.role === 'MODERATOR').length || 0}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Filters and Search */}
      <div 
        className="p-6 rounded-xl shadow-sm border w-full"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="flex flex-col sm:flex-row gap-4 w-full">
          {/* Search */}
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
              <input
                type="text"
                placeholder="Search users by name, username, or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2"
                style={{
                  backgroundColor: 'var(--color-bg-primary)',
                  borderColor: 'var(--color-border)',
                  color: 'var(--color-text-primary)',
                  '--tw-ring-color': 'var(--color-accent)',
                  '--tw-ring-offset-color': 'var(--color-bg-primary)'
                } as React.CSSProperties}
              />
            </div>
          </div>

          {/* Role Filter */}
          <div className="sm:w-48">
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)'
              } as React.CSSProperties}
            >
              <option value="ALL">All Roles</option>
              {ROLES.map(role => (
                <option key={role.value} value={role.value}>{role.label}</option>
              ))}
            </select>
          </div>

          {/* Status Filter */}
          <div className="sm:w-48">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full px-3 py-2 border rounded-lg focus:ring-2"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                borderColor: 'var(--color-border)',
                color: 'var(--color-text-primary)',
                '--tw-ring-color': 'var(--color-accent)',
                '--tw-ring-offset-color': 'var(--color-bg-primary)'
              } as React.CSSProperties}
            >
              <option value="ALL">All Status</option>
              <option value="ACTIVE">Active</option>
              <option value="INACTIVE">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div 
        className="rounded-xl shadow-sm border overflow-hidden w-full"
        style={{
          backgroundColor: 'var(--color-bg-primary)',
          borderColor: 'var(--color-border)',
          boxShadow: 'var(--shadow-sm)'
        }}
      >
        <div className="w-full overflow-x-auto">
          <table className="w-full min-w-[980px] divide-y" style={{ borderColor: 'var(--color-border)', tableLayout: 'fixed' }}>
            <thead style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '24%' }}>
                  User
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '11%' }}>
                  Role
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '7%' }}>
                  Customers
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '10%' }}>
                  Status
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '11%' }}>
                  Last Login
                </th>
                <th className="px-3 py-3 text-left text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '11%' }}>
                  Created
                </th>
                <th className="px-3 py-3 text-right text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--color-text-muted)', width: '26%' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y" style={{ borderColor: 'var(--color-border)' }}>
              {filteredUsers.map((user) => (
                <tr 
                  key={user.id} 
                  className="transition-colors"
                  style={{ backgroundColor: 'var(--color-bg-primary)' }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-secondary)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'var(--color-bg-primary)';
                  }}
                >
                  <td className="px-4 py-4">
                    <div className="flex items-center min-w-0">
                      <div className="flex-shrink-0 h-10 w-10">
                        <div 
                          className="h-10 w-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: 'var(--color-accent)' }}
                        >
                          <span className="text-sm font-medium" style={{ color: 'white' }}>
                            {(user.firstName?.[0] || user.username[0] || 'U').toUpperCase()}
                            {(user.lastName?.[0] || user.username[1] || '').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-4 min-w-0 flex-1">
                        <div className="text-sm font-medium truncate" style={{ color: 'var(--color-text-primary)' }}>
                          {user.firstName && user.lastName 
                            ? `${user.firstName} ${user.lastName}` 
                            : user.username}
                        </div>
                        <div className="text-sm flex items-center min-w-0" style={{ color: 'var(--color-text-secondary)' }}>
                          <Mail className="w-3 h-3 mr-1 flex-shrink-0" />
                          <span className="truncate">{user.email}</span>
                        </div>
                        {user.username !== (user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username) && (
                          <div className="text-xs truncate" style={{ color: 'var(--color-text-muted)' }}>@{user.username}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center min-w-0">
                      {getRoleIcon(user.role)}
                      <span className="ml-2 text-sm font-medium truncate" style={{ color: getRoleColor(user.role) }}>
                        {ROLES.find(r => r.value === user.role)?.label}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm" style={{ color: 'var(--color-text-muted)' }}>
                    {user.role === 'ADMINISTRATOR' ? (
                      <span>All</span>
                    ) : (
                      <span>{user.customerAccessCount ?? 0}</span>
                    )}
                  </td>
                  <td className="px-3 py-4">
                    <div className="flex items-center min-w-0">
                      <span 
                        className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium whitespace-nowrap"
                        style={{
                          backgroundColor: user.isActive 
                            ? 'var(--color-success-bg)' 
                            : 'var(--color-error-bg)',
                          color: user.isActive 
                            ? 'var(--color-success-text)' 
                            : 'var(--color-error-text)'
                        }}
                      >
                        {user.isActive ? (
                          <>
                            <UserCheck className="w-3 h-3 mr-1 flex-shrink-0" />
                            Active
                          </>
                        ) : (
                          <>
                            <UserX className="w-3 h-3 mr-1 flex-shrink-0" />
                            Inactive
                          </>
                        )}
                      </span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm overflow-hidden" style={{ color: 'var(--color-text-muted)' }}>
                    <div className="flex items-center min-w-0">
                      <LogIn className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{formatDate(user.lastLoginAt)}</span>
                    </div>
                  </td>
                  <td className="px-3 py-4 text-sm overflow-hidden" style={{ color: 'var(--color-text-muted)' }}>
                    <div className="flex items-center min-w-0">
                      <Calendar className="w-3 h-3 mr-1 flex-shrink-0" />
                      <span className="truncate">{formatDate(user.createdAt)}</span>
                    </div>
                  </td>
                  <td className="px-2 py-4 text-right text-sm font-medium overflow-hidden">
                    <div className="flex items-center justify-end gap-0.5 flex-nowrap">
                      <Tooltip 
                        content={
                          user.id === currentUser?.id 
                            ? "You cannot deactivate your own account"
                            : user.isActive 
                              ? "Deactivate user account" 
                              : "Activate user account"
                        }
                      >
                        <button
                          onClick={() => toggleUserStatus(user)}
                          disabled={user.id === currentUser?.id}
                          className={`p-1.5 rounded-lg transition-colors hover:opacity-70 ${
                            user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          style={{ 
                            color: user.isActive 
                              ? 'var(--color-error)' 
                              : 'var(--color-success)' 
                          }}
                        >
                          {user.isActive ? <UserX className="w-4 h-4" /> : <UserCheck className="w-4 h-4" />}
                        </button>
                      </Tooltip>
                      <Tooltip content="Edit user details and permissions">
                        <button
                          onClick={() => openEditModal(user)}
                          className="p-1.5 hover:opacity-70 rounded-lg transition-colors"
                          style={{ color: '#6366f1' }}
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      <Tooltip 
                        content={
                          user.authProvider === 'ENTRA_AD'
                            ? "Password is managed by Microsoft Entra ID"
                            : "Change user password"
                        }
                      >
                        <button
                          onClick={() => openPasswordModal(user)}
                          disabled={user.authProvider === 'ENTRA_AD'}
                          className={`p-1.5 hover:opacity-70 rounded-lg transition-colors ${
                            user.authProvider === 'ENTRA_AD' ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          style={{ color: '#f59e0b' }}
                        >
                          <Key className="w-4 h-4" />
                        </button>
                      </Tooltip>
                      {user.role !== 'ADMINISTRATOR' && (
                        <Tooltip content="Manage customer access">
                          <button
                            onClick={() => openCustomerAccessModal(user)}
                            className="p-1.5 hover:opacity-70 rounded-lg transition-colors"
                            style={{ color: '#0d9488' }}
                          >
                            <Building2 className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      )}
                      {settings?.notesEnabled && (
                        <Tooltip content="Manage user's notes">
                          <button
                            onClick={() => openNotesModal(user)}
                            className="p-1.5 hover:opacity-70 rounded-lg transition-colors"
                            style={{ color: '#2563eb' }}
                          >
                            <FileText className="w-4 h-4" />
                          </button>
                        </Tooltip>
                      )}
                      <Tooltip 
                        content={
                          user.id === currentUser?.id 
                            ? "You cannot delete your own account"
                            : "Delete user permanently"
                        }
                      >
                        <button
                          onClick={() => openDeleteModal(user)}
                          disabled={user.id === currentUser?.id}
                          className={`p-1.5 hover:opacity-70 rounded-lg transition-colors ${
                            user.id === currentUser?.id ? 'opacity-50 cursor-not-allowed' : ''
                          }`}
                          style={{ color: 'var(--color-error)' }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </Tooltip>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredUsers.length === 0 && (
          <div className="text-center py-12">
            <Users className="w-12 h-12 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>No users found</h3>
            <p style={{ color: 'var(--color-text-secondary)' }}>Try adjusting your search or filter criteria.</p>
          </div>
        )}
      </div>

      {/* Create User Modal */}
      <AnimatePresence>
        {showCreateModal && (
          <Modal
            open={showCreateModal}
            title="Create New User"
            onClose={() => setShowCreateModal(false)}
            maxWidthClassName="max-w-md"
          >
            <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={createForm.firstName}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-accent)'
                        } as any}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={createForm.lastName}
                        onChange={(e) => setCreateForm(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-accent)'
                        } as any}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      value={createForm.username}
                      onChange={(e) => {
                        setCreateForm(prev => ({ ...prev, username: e.target.value }));
                        setCreateFieldErrors(prev => ({ ...prev, username: undefined }));
                        setCreateError(null);
                      }}
                      onBlur={() => handleCreateFieldBlur('username')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: createFieldErrors.username ? 'var(--color-error-border)' : 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                      required
                    />
                    {createFieldErrors.username && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-error-text)' }}>
                        {createFieldErrors.username}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={createForm.email}
                      onChange={(e) => {
                        setCreateForm(prev => ({ ...prev, email: e.target.value }));
                        setCreateFieldErrors(prev => ({ ...prev, email: undefined }));
                        setCreateError(null);
                      }}
                      onBlur={() => handleCreateFieldBlur('email')}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: createFieldErrors.email ? 'var(--color-error-border)' : 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                      required
                    />
                    {createFieldErrors.email && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-error-text)' }}>
                        {createFieldErrors.email}
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Role *
                    </label>
                    <select
                      value={createForm.role}
                      onChange={(e) => setCreateForm(prev => ({ ...prev, role: e.target.value as any }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                  </div>

                  <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
                    A welcome email with a generated password will be sent automatically using the Welcome email template. The template must include <code>{'{password}'}</code>.
                  </p>

                  {/* Auth provider is automatically set to LOCAL by server */}
                </div>

                {createError && (
                  <div className="mt-4 p-3 rounded-lg" style={{ backgroundColor: 'var(--color-error-bg)', border: '1px solid var(--color-error-border)' }}>
                    <p className="text-sm" style={{ color: 'var(--color-error-text)' }}>{createError}</p>
                  </div>
                )}

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <button
                    onClick={() => setShowCreateModal(false)}
                    className="px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleCreateUser}
                    disabled={
                      !createForm.username ||
                      !createForm.email ||
                      createUserMutation.isPending ||
                      Boolean(createFieldErrors.email || createFieldErrors.username)
                    }
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    style={{
                      backgroundColor: 'var(--color-accent)'
                    }}
                  >
                    {createUserMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Create User
                  </button>
            </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Edit User Modal */}
      <AnimatePresence>
        {showEditModal && selectedUser && (
          <Modal
            open={showEditModal}
            title="Edit User"
            onClose={() => setShowEditModal(false)}
            maxWidthClassName="max-w-md"
          >
            <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                        First Name
                      </label>
                      <input
                        type="text"
                        value={editForm.firstName || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, firstName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-accent)'
                        } as any}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                        Last Name
                      </label>
                      <input
                        type="text"
                        value={editForm.lastName || ''}
                        onChange={(e) => setEditForm(prev => ({ ...prev, lastName: e.target.value }))}
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                        style={{
                          backgroundColor: 'var(--color-bg-secondary)',
                          borderColor: 'var(--color-border)',
                          color: 'var(--color-text-primary)',
                          '--tw-ring-color': 'var(--color-accent)'
                        } as any}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Username *
                    </label>
                    <input
                      type="text"
                      value={editForm.username || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Email *
                    </label>
                    <input
                      type="email"
                      value={editForm.email || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, email: e.target.value }))}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Role *
                    </label>
                    <select
                      value={editForm.role || ''}
                      onChange={(e) => setEditForm(prev => ({ ...prev, role: e.target.value as any }))}
                      disabled={selectedUser.id === currentUser?.id}
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 transition-colors disabled:opacity-50"
                      style={{
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        '--tw-ring-color': 'var(--color-accent)'
                      } as any}
                    >
                      {ROLES.map(role => (
                        <option key={role.value} value={role.value}>{role.label}</option>
                      ))}
                    </select>
                    {selectedUser.id === currentUser?.id && (
                      <p className="text-xs mt-1" style={{ color: 'var(--color-text-muted)' }}>You cannot change your own role</p>
                    )}
                  </div>

                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="isActive"
                      checked={editForm.isActive || false}
                      onChange={(e) => setEditForm(prev => ({ ...prev, isActive: e.target.checked }))}
                      disabled={selectedUser.id === currentUser?.id}
                      className="h-4 w-4 rounded disabled:opacity-50"
                      style={{
                        accentColor: 'var(--color-accent)',
                        borderColor: 'var(--color-border)'
                      }}
                    />
                    <label htmlFor="isActive" className="ml-2 block text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      Active User
                    </label>
                    {selectedUser.id === currentUser?.id && (
                      <span className="ml-2 text-xs" style={{ color: 'var(--color-text-muted)' }}>(Cannot deactivate yourself)</span>
                    )}
                  </div>

                  <div className="pt-2 border-t" style={{ borderColor: 'var(--color-border)' }}>
                    <h4 className="text-sm font-semibold mb-3" style={{ color: 'var(--color-text-primary)' }}>
                      Environment Access
                    </h4>
                    <div className="space-y-3">
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="canAccessDemo"
                          checked={editForm.canAccessDemo || false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, canAccessDemo: e.target.checked }))}
                          disabled={environmentName !== 'Production'}
                          className="h-4 w-4 rounded"
                          style={{
                            accentColor: 'var(--color-accent)',
                            borderColor: 'var(--color-border)',
                            opacity: environmentName !== 'Production' ? 0.5 : 1,
                            cursor: environmentName !== 'Production' ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <label 
                          htmlFor="canAccessDemo" 
                          className="ml-2 block text-sm" 
                          style={{ 
                            color: environmentName !== 'Production' ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'
                          }}
                        >
                          Can Access Demo Environment
                        </label>
                      </div>
                      <div className="flex items-center">
                        <input
                          type="checkbox"
                          id="canAccessPreProd"
                          checked={editForm.canAccessPreProd || false}
                          onChange={(e) => setEditForm(prev => ({ ...prev, canAccessPreProd: e.target.checked }))}
                          disabled={environmentName !== 'Production'}
                          className="h-4 w-4 rounded"
                          style={{
                            accentColor: 'var(--color-accent)',
                            borderColor: 'var(--color-border)',
                            opacity: environmentName !== 'Production' ? 0.5 : 1,
                            cursor: environmentName !== 'Production' ? 'not-allowed' : 'pointer'
                          }}
                        />
                        <label 
                          htmlFor="canAccessPreProd" 
                          className="ml-2 block text-sm" 
                          style={{ 
                            color: environmentName !== 'Production' ? 'var(--color-text-muted)' : 'var(--color-text-secondary)'
                          }}
                        >
                          Can Access PreProduction Environment
                        </label>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3 mt-6 pt-4 border-t" style={{ borderColor: 'var(--color-border)' }}>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleUpdateUser}
                    disabled={updateUserMutation.isPending}
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    style={{
                      backgroundColor: 'var(--color-accent)'
                    }}
                  >
                    {updateUserMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Save className="w-4 h-4 mr-2" />
                    )}
                    Update User
                  </button>
                </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Delete User Modal */}
      <AnimatePresence>
        {showDeleteModal && selectedUser && (
          <Modal
            open={showDeleteModal}
            title="Delete User"
            onClose={() => setShowDeleteModal(false)}
            maxWidthClassName="max-w-md"
          >
            <div className="mb-4">
                  <div className="flex items-center mb-4">
                    <AlertTriangle className="w-8 h-8 text-red-500 mr-3" />
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        Are you sure you want to delete this user?
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        This action cannot be undone.
                      </p>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent)' }}>
                          <span className="text-xs font-medium text-white">
                            {(selectedUser.firstName?.[0] || selectedUser.username[0] || 'U').toUpperCase()}
                            {(selectedUser.lastName?.[0] || selectedUser.username[1] || '').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {selectedUser.firstName && selectedUser.lastName 
                            ? `${selectedUser.firstName} ${selectedUser.lastName}` 
                            : selectedUser.username}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{selectedUser.email}</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteUser}
                    disabled={deleteUserMutation.isPending}
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    style={{
                      backgroundColor: 'var(--color-error)'
                    }}
                  >
                    {deleteUserMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Delete User
                  </button>
                </div>
          </Modal>
        )}
      </AnimatePresence>

      {/* Change Password Modal */}
      <AnimatePresence>
        {showPasswordModal && selectedUser && (
          <Modal
            open={showPasswordModal}
            title={
              <span className="flex items-center">
                <Key className="w-5 h-5 text-amber-600 mr-2" />
                Reset Password
              </span> as any
            }
            onClose={() => setShowPasswordModal(false)}
            maxWidthClassName="max-w-md"
          >
            <div className="mb-4">
                  <div className="flex items-center mb-4">
                    <AlertTriangle className="w-8 h-8 mr-3" style={{ color: '#f59e0b' }} />
                    <div>
                      <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        Reset this user&apos;s password?
                      </p>
                      <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                        A new password will be generated automatically and emailed to the user.
                      </p>
                    </div>
                  </div>

                  <div className="p-3 rounded-lg" style={{ backgroundColor: 'var(--color-bg-secondary)' }}>
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-8 w-8">
                        <div className="h-8 w-8 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-accent)' }}>
                          <span className="text-xs font-medium text-white">
                            {(selectedUser.firstName?.[0] || selectedUser.username[0] || 'U').toUpperCase()}
                            {(selectedUser.lastName?.[0] || selectedUser.username[1] || '').toUpperCase()}
                          </span>
                        </div>
                      </div>
                      <div className="ml-3">
                        <p className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                          {selectedUser.firstName && selectedUser.lastName 
                            ? `${selectedUser.firstName} ${selectedUser.lastName}` 
                            : selectedUser.username}
                        </p>
                        <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>{selectedUser.email}</p>
                      </div>
                    </div>
                  </div>

                  {passwordResetError && (
                    <div
                      className="mt-4 px-4 py-3 rounded-lg text-sm"
                      style={{
                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                        border: '1px solid rgba(239, 68, 68, 0.3)',
                        color: '#ef4444',
                      }}
                    >
                      {passwordResetError}
                    </div>
                  )}
                </div>

                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setShowPasswordModal(false)}
                    className="px-4 py-2 border rounded-lg transition-colors"
                    style={{
                      backgroundColor: 'var(--color-bg-secondary)',
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordChange}
                    disabled={resetUserPasswordMutation.isPending}
                    className="px-4 py-2 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                    style={{
                      backgroundColor: 'var(--color-accent)'
                    }}
                  >
                    {resetUserPasswordMutation.isPending ? (
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    ) : (
                      <Key className="w-4 h-4 mr-2" />
                    )}
                    Reset Password
                  </button>
                </div>
          </Modal>
        )}

        {/* User Notes Modal */}
        {showNotesModal && selectedUser && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Manage Notes for {selectedUser.firstName && selectedUser.lastName 
                        ? `${selectedUser.firstName} ${selectedUser.lastName}` 
                        : selectedUser.username}
                    </h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      View and manage all notes created by this user
                    </p>
                  </div>
                  <button
                    onClick={() => setShowNotesModal(false)}
                    style={{ color: 'var(--color-text-muted)' }}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                {notesLoading ? (
                  <div className="flex items-center justify-center py-12">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: 'var(--color-accent)' }}></div>
                    <span className="ml-3" style={{ color: 'var(--color-text-secondary)' }}>Loading notes...</span>
                  </div>
                ) : (userNotesData && Array.isArray(userNotesData) && userNotesData.length > 0) ? (
                  <div className="space-y-2">
                    {(userNotesData || []).map((note: Note) => (
                      <div
                        key={note.id}
                        className="border rounded-lg p-3 hover:shadow-md transition-shadow cursor-pointer"
                        style={{
                          borderColor: 'var(--color-border)',
                          backgroundColor: 'var(--color-bg-secondary)'
                        }}
                        onClick={() => {
                          setSelectedNote(note);
                          setShowNoteDetailsModal(true);
                        }}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <h4 className="text-lg font-medium mb-1" style={{ color: 'var(--color-text-primary)' }}>
                              {note.title}
                            </h4>
                            <div className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                              Created: {new Date(note.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <span 
                              className="px-2 py-1 rounded-full text-xs"
                              style={{
                                backgroundColor: note.isPublic 
                                  ? 'var(--color-success-bg)' 
                                  : 'var(--color-bg-tertiary)',
                                color: note.isPublic 
                                  ? 'var(--color-success-text)' 
                                  : 'var(--color-text-secondary)'
                              }}
                            >
                              {note.isPublic ? 'Public' : 'Private'}
                            </span>
                            <FileText className="w-4 h-4" style={{ color: 'var(--color-text-muted)' }} />
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <FileText className="w-16 h-16 mx-auto mb-4" style={{ color: 'var(--color-text-muted)' }} />
                    <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--color-text-primary)' }}>No notes found</h3>
                    <p style={{ color: 'var(--color-text-secondary)' }}>
                      This user hasn't created any notes yet.
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Note Details Modal */}
        {showNoteDetailsModal && selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)'
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto dark-scrollbar"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      {selectedNote.title}
                    </h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Note Details
                    </p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setShowShareModal(true)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--color-accent)',
                        color: 'white'
                      }}
                    >
                      <UserCheck className="w-4 h-4" />
                      <span>Share</span>
                    </button>
                    <button
                      onClick={() => setShowDeleteConfirmModal(true)}
                      className="flex items-center space-x-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--color-destructive)',
                        color: 'white'
                      }}
                    >
                      <Trash2 className="w-4 h-4" />
                      <span>Delete</span>
                    </button>
                    <button
                      onClick={() => {
                        setShowNoteDetailsModal(false);
                        setSelectedNote(null);
                      }}
                      style={{ color: 'var(--color-text-muted)' }}
                      className="hover:opacity-70 transition-opacity"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Note Metadata */}
                  <div className="flex items-center space-x-4 text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                    <span>Created: {new Date(selectedNote.createdAt).toLocaleDateString()}</span>
                    <span>Updated: {new Date(selectedNote.updatedAt).toLocaleDateString()}</span>
                    <span 
                      className="px-2 py-1 rounded-full text-xs"
                      style={{
                        backgroundColor: selectedNote.isPublic 
                          ? 'var(--color-success-bg)' 
                          : 'var(--color-bg-tertiary)',
                        color: selectedNote.isPublic 
                          ? 'var(--color-success-text)' 
                          : 'var(--color-text-secondary)'
                      }}
                    >
                      {selectedNote.isPublic ? 'Public' : 'Private'}
                    </span>
                  </div>

                  {/* Note Content */}
                  <div className="border rounded-lg p-4" style={{ borderColor: 'var(--color-border)' }}>
                    <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Content:</h4>
                    <div 
                      className="prose prose-sm max-w-none"
                      style={{ color: 'var(--color-text-primary)' }}
                      dangerouslySetInnerHTML={{ __html: selectedNote.content }}
                    />
                  </div>

                  {/* Tags */}
                  {selectedNote.tags && selectedNote.tags.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Tags:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedNote.tags.map((tag: string, index: number) => (
                          <span
                            key={index}
                            className="px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor: 'var(--color-accent)',
                              color: 'white'
                            }}
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Shared With */}
                  {selectedNote.sharedWith && selectedNote.sharedWith.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium mb-2" style={{ color: 'var(--color-text-secondary)' }}>Shared with:</h4>
                      <div className="flex flex-wrap gap-2">
                        {selectedNote.sharedWith.map((share: any) => (
                          <span
                            key={share.id}
                            className="px-2 py-1 text-xs rounded-full"
                            style={{
                              backgroundColor: share.canEdit 
                                ? 'var(--color-accent)' 
                                : 'var(--color-bg-tertiary)',
                              color: share.canEdit 
                                ? 'white' 
                                : 'var(--color-text-secondary)'
                            }}
                          >
                            {share.user.username} ({share.canEdit ? 'Can Edit' : 'View Only'})
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Delete Confirmation Modal */}
        {showDeleteConfirmModal && selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)'
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-md w-full"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: 'var(--color-destructive)' }}>
                    <AlertTriangle className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Delete Note
                    </h3>
                    <p className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
                      This action cannot be undone.
                    </p>
                  </div>
                </div>
                
                <p className="mb-6" style={{ color: 'var(--color-text-primary)' }}>
                  Are you sure you want to delete "<strong>{selectedNote.title}</strong>"? This will permanently remove the note and all its content.
                </p>

                <div className="flex space-x-3">
                  <button
                    onClick={() => setShowDeleteConfirmModal(false)}
                    className="flex-1 px-4 py-2 rounded-lg border transition-colors"
                    style={{
                      borderColor: 'var(--color-border)',
                      color: 'var(--color-text-primary)',
                      backgroundColor: 'var(--color-bg-secondary)'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => {
                      deleteNoteMutation.mutate(selectedNote.id);
                    }}
                    className="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
                    style={{
                      backgroundColor: 'var(--color-destructive)',
                      color: 'white'
                    }}
                    disabled={deleteNoteMutation.isPending}
                  >
                    {deleteNoteMutation.isPending ? 'Deleting...' : 'Delete Note'}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}

        {/* Share Note Modal */}
        {showShareModal && selectedNote && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            style={{
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              backdropFilter: 'blur(4px)'
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="rounded-xl shadow-xl max-w-md w-full"
              style={{
                backgroundColor: 'var(--color-bg-primary)',
                boxShadow: 'var(--shadow-xl)'
              }}
            >
              <div className="p-6">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h3 className="text-lg font-semibold" style={{ color: 'var(--color-text-primary)' }}>
                      Share Note
                    </h3>
                    <p className="text-sm mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      Share "{selectedNote.title}" with another user
                    </p>
                  </div>
                  <button
                    onClick={() => {
                      setShowShareModal(false);
                      setShareForm({ userId: '', canEdit: false });
                    }}
                    style={{ color: 'var(--color-text-muted)' }}
                    className="hover:opacity-70 transition-opacity"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-2" style={{ color: 'var(--color-text-primary)' }}>
                      Select User
                    </label>
                    <select
                      value={shareForm.userId}
                      onChange={(e) => setShareForm({ ...shareForm, userId: e.target.value })}
                      className="w-full px-3 py-2 rounded-lg border transition-colors"
                      style={{
                        borderColor: 'var(--color-border)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        color: 'var(--color-text-primary)'
                      }}
                    >
                      <option value="">Choose a user...</option>
                      {usersData?.filter(user => user.id !== selectedNote.author.id).map((user) => (
                        <option key={user.id} value={user.id}>
                          {user.firstName && user.lastName ? `${user.firstName} ${user.lastName}` : user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={shareForm.canEdit}
                        onChange={(e) => setShareForm({ ...shareForm, canEdit: e.target.checked })}
                        className="rounded"
                      />
                      <span className="text-sm font-medium" style={{ color: 'var(--color-text-primary)' }}>
                        Allow editing
                      </span>
                    </label>
                    <p className="text-xs mt-1" style={{ color: 'var(--color-text-secondary)' }}>
                      If unchecked, the user will only be able to view the note
                    </p>
                  </div>

                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={() => {
                        setShowShareModal(false);
                        setShareForm({ userId: '', canEdit: false });
                      }}
                      className="flex-1 px-4 py-2 rounded-lg border transition-colors"
                      style={{
                        borderColor: 'var(--color-border)',
                        color: 'var(--color-text-primary)',
                        backgroundColor: 'var(--color-bg-secondary)'
                      }}
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        if (shareForm.userId) {
                          shareNoteMutation.mutate({
                            noteId: selectedNote.id,
                            userId: shareForm.userId,
                            canEdit: shareForm.canEdit
                          });
                        }
                      }}
                      className="flex-1 px-4 py-2 rounded-lg font-medium transition-colors"
                      style={{
                        backgroundColor: 'var(--color-accent)',
                        color: 'white'
                      }}
                      disabled={!shareForm.userId || shareNoteMutation.isPending}
                    >
                      {shareNoteMutation.isPending ? 'Sharing...' : 'Share Note'}
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <UserCustomerAccessModal
        open={showCustomerAccessModal}
        user={selectedUser}
        allUsers={usersData ?? []}
        onClose={() => {
          setShowCustomerAccessModal(false);
          setSelectedUser(null);
        }}
        onUpdated={() => {
          queryClient.invalidateQueries({ queryKey: ['users'] });
        }}
      />
    </div>
  );
};
